/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { Box, Typography, Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { lockIcon } from '../../../../components/common';
import { useModule } from '../../../../../../utils/useModule';
import useOrgLevelAdmin from '../../../../../../hooks/useOrgLevelAdmin';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { breadCrumbsActions } from '../../../../../../redux/slices/breadcrumbs/breadcrumbsSlice';
import ROLES from '../../../../../../utils/roles';
import { getUserDetails } from '../../../../../../utils/orgName';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import useResponsiveScreenSwitch from '../../../../../../utils/useResponsiveScreenSwitch';
import useGroupSettings from '../../../../../../hooks/useGroupSettigs';
import useOrgSettings from '../../../../../../hooks/useOrgSettings';
import { groupLoaderActions } from '../../../../../../redux/slices/groupLoaderSlice';
import useGroupDetails from '../../../../../../queries/useGroupDetails';
import useGroupList from '../../../../../../queries/useGroupList';
import ProgramTableLoader from '../../../../../../Loader/ProgramTableLoader';
import Logo from '../../../../../../assets/images/brand/dosen/logo.png';

interface TabWithLockIconProps {
  tabName: string;
  isActive: boolean;
  onClick: () => void;
  tabDisable?: boolean;
  lockTooltip?: string;
}

/* ===========================================================
   HEADER TITLE DINÂMICO PELO PATH
   =========================================================== */
function useDynamicHeaderTitle() {
  const location = useLocation();

  return useMemo(() => {
    if (location.pathname.includes('/overview')) return 'ELLIOT';
    if (location.pathname.includes('/users')) return 'People';
    if (location.pathname.includes('/settings')) return 'PROGRAM MANAGEMENT';
    if (location.pathname.includes('/insights')) return 'Insights';
    if (location.pathname.includes('/matches')) return 'Matches';
    return 'Elliott';
  }, [location.pathname]);
}

/* ===========================================================
   DRAWER LATERAL — IGUAL AO FIGMA
   =========================================================== */
function ProgramSideDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="left"
      PaperProps={{
        sx: {
          width: 300,
          bgcolor: '#0F0F0F',
          borderRight: '1px solid #2A2A2A',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* Drawer Header */}
      <Box className="flex items-center justify-between w-full pr-2">
        <Box className="p-3 flex items-center">
          <Box className="w-9 h-9 flex items-center justify-center relative">
            <Box
              sx={{
                width: 18,
                height: 18,
                position: 'absolute',
                outline: '2px solid #FDFDFD',
                outlineOffset: '-1px',
                left: 'calc(50% - 9px)',
                top: 'calc(50% - 9px)'
              }}
            />
          </Box>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: '#868686' }} />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box className="flex flex-col gap-4 mt-4 overflow-y-auto pr-2" sx={{ flexGrow: 1 }}>
        {['Programs', 'Insights', 'People', 'Calendar', 'Settings'].map((item) => (
          <Box key={item} className="h-11 px-3 rounded flex items-center gap-4 cursor-pointer hover:bg-[#1A1A1A]">
            <span className="text-[#FDFDFD] text-[16px] font-normal uppercase">{item}</span>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}

/* ===========================================================
   HEADER PRINCIPAL (FIGMA)
   =========================================================== */
function ProgramHeader() {
  const title = useDynamicHeaderTitle();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          height: 86,
          borderBottom: '1px solid #2A2A2A',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: '#0F0F0F'
        }}
      >
        <IconButton onClick={() => setOpen(true)}>
          <MenuIcon sx={{ color: '#FDFDFD' }} />
        </IconButton>

        <Box className="flex-1 flex items-center justify-center gap-2">
          <span className="text-white text-[20px] uppercase font-normal leading-[22px]">{title}</span>
        </Box>

        <img src={Logo} alt="Dosen Logo" style={{ width: 92, height: 18, objectFit: 'contain' }} />
      </Box>

      <ProgramSideDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/* ===========================================================
   WRAPPER — FUNDO PRETO + SCROLL INTERNO
   =========================================================== */
function ProgramDetailsWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#0F0F0F',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <ProgramHeader />

      {/* Conteúdo com scroll */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
}

/* ===========================================================
   FILE ORIGINAL (ProgramDetails) AGORA COM WRAPPER NOVO
   =========================================================== */
const ProgramDetails = () => {
  const orgGroupData = useAppSelector((state) => state.orgLevelGroup);
  const orgDetails = useAppSelector((state) => state.orgDetails);
  const allGroupData = useAppSelector((state) => state.allGroups);
  const mainGroup = useAppSelector((state) => state.allGroups.data?.mainGroup);
  const breadcrumbs = useAppSelector((state) => state.breadCrumbs.breadcrumbs);
  const isGoal = useAppSelector((state) => state.groupDetails.data?.goalPublished);
  const { isMasterclass, isMentorship } = useModule();
  const isGroupUnlocked = isMentorship ? true : isGoal;

  const dispatch = useAppDispatch();
  const { forMobile } = useResponsiveScreenSwitch();
  const { groupId: grpId, orgId, coId, location } = useProgramContext();
  const [searchParams] = useSearchParams();
  const role = localStorage.getItem('role');
  let orgName = searchParams.get('org') || '';
  const [groupType, setGroupType] = useState('');

  const currentPath = useLocation();
  let param = role === ROLES.platAdmin ? `/?org=${orgName}` : '';

  const groupDetails = useAppSelector((state) => state.groupDetails);
  const groupRoleList = useAppSelector((state) => state.groupRoleList);
  const orgLevelAdmin = useOrgLevelAdmin();
  const programModel = groupDetails.data?.model;
  // Tab visibility under the restructure: Designer = anything with lessons
  // (Course / Guided Course / Hybrid); Matches = anything with matching
  // (Mentorship / Guided Course / Hybrid).
  const hasDesigner = programModel !== 'mentorship';
  const hasMatches = programModel !== 'masterclass';
  const groupSettings = useGroupSettings();

  const { refetch } = useGroupDetails(grpId, location);
  const { refetch: fetchGroups } = useGroupList({ orgId: coId, grpId, location });

  useEffect(() => {
    if (allGroupData.data) {
      if (mainGroup) {
        const mainGroup = breadcrumbs.find((each) => each.name === 'Program');
        dispatch(breadCrumbsActions.removeBreadCrumbs(mainGroup));
      } else {
        dispatch(
          breadCrumbsActions.updateBreadCrumbs({
            id: grpId,
            name: allGroupData.data.mainGroupName,
            url: `/app/programdetails/${orgId}/${coId}/${grpId}/overview${param}`
          })
        );
      }
    }
  }, [mainGroup, allGroupData.data]);

  useEffect(() => {
    if (groupDetails?.data) {
      setGroupType(groupDetails?.data?.type);
    }
  }, [groupDetails?.data]);

  useEffect(() => {
    const newGrpId = grpId === 'admin' ? '' : grpId;
    fetchGroups();
    if (newGrpId) {
      refetch();
    }
    dispatch(groupLoaderActions.updateState(false));
  }, [grpId]);

  const activeTab = currentPath.pathname
    .split('/')
    .find((each) => ['users', 'settings', 'insights', 'matches', 'overview', 'designer'].includes(each));
  const url = `/app/programdetails/${orgId}/${coId}/${grpId}`;

  const navigate = useNavigate();
  const orgSettings = useOrgSettings();
  // const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);

  // No fullscreen loader: each routed page renders structural skeletons inline.
  // Render through immediately — even with empty Redux slices, children handle
  // their own loading state via React Query / local skeletons.
  if (orgGroupData.error) {
    return <Box>{orgGroupData.errorText}</Box>;
  }
  if (orgDetails.error) {
    return <Box>{orgDetails.errorText}</Box>;
  }

  /** Tabs design with all requrments */
  function createTabWithLockIcon({ tabName, isActive, onClick, tabDisable, lockTooltip }: TabWithLockIconProps) {
    const TabDisableValue = groupType === 'level' ? (tabName !== 'Overview' ? true : false) : tabDisable;

    return (
      <Box
        pb={1}
        gap="3px"
        display="flex"
        alignItems="center"
        onClick={TabDisableValue ? undefined : onClick}
        borderBottom={isActive ? '3px solid #DF6438' : undefined}
        className={`cursor-${TabDisableValue ? 'not-allowed' : 'pointer'}`}
      >
        {TabDisableValue && lockIcon({ title: lockTooltip })}

        <Typography
          lineHeight="normal"
          fontWeight={isActive ? '700' : '400'}
          color={isActive ? '#DF6438' : 'primary'}
          className={TabDisableValue ? 'disabled-box-opacity-25' : ''}
        >
          {tabName}
        </Typography>
      </Box>
    );
  }

  const getTabs = () => {
    if (!mainGroup) {
      return (
        <Box borderBottom="1px solid #EFF0F4" mb={2}>
          <Box display="flex" width="100%" overflow="auto">
            {breadcrumbs.map((each, index) => (
              <Fragment key={index}>
                {index === breadcrumbs.length - 1 ? (
                  <Box>
                    <Typography variant="caption" noWrap>
                      {each.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Link to={each.url} onClick={() => dispatch(groupLoaderActions.updateState(true))}>
                      <Typography noWrap variant="caption" fontWeight="400" color="secondary">
                        {each.name}
                      </Typography>
                    </Link>
                    <Typography variant="caption" fontWeight="400" mx={0.5} color="secondary">
                      /
                    </Typography>
                  </Box>
                )}
              </Fragment>
            ))}
          </Box>

          {/* Group header with tabs */}
          <Box
            display={forMobile ? 'block' : 'flex'}
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            mt={1}
          >
            <Typography variant="h3" noWrap mb={1} mt="8px" lineHeight="1.3">
              {_.get(allGroupData, 'data.mainGroupName', '')}
            </Typography>

            <Box display="flex" columnGap="40px">
              {/* Overview */}
              {createTabWithLockIcon({
                tabName: 'Overview',
                isActive: activeTab === 'overview',
                onClick: () => navigate(`${url}/overview${param}`),
                tabDisable: !(groupSettings && isGroupUnlocked)
              })}
              {/* Insights */}
              {groupDetails.data?.type !== 'level' &&
                createTabWithLockIcon({
                  tabName: 'Insights',
                  isActive: activeTab === 'insights',
                  onClick: () => navigate(`/app/programdetails/${orgId}/${coId}/admin/insights${param}`),
                  tabDisable: !(groupSettings && isGroupUnlocked)
                })}
              {orgLevelAdmin ? (
                <>
                  {/* Designer — Course / Guided Course / Hybrid */}
                  {groupDetails.data?.type !== 'level' &&
                    hasDesigner &&
                    createTabWithLockIcon({
                      tabName: 'Designer',
                      isActive: activeTab === 'designer',
                      onClick: () => navigate(`${url}/designer${param}`),
                      tabDisable: !(groupSettings && isGroupUnlocked)
                    })}
                  {/* Matches — Mentorship / Guided Course / Hybrid */}
                  {groupDetails.data?.type !== 'level' &&
                    hasMatches &&
                    !forMobile &&
                    createTabWithLockIcon({
                      tabName: 'Matches',
                      isActive: activeTab === 'matches',
                      onClick: () => navigate(`${url}/matches${param}`),
                      tabDisable: !(groupSettings && isGroupUnlocked)
                    })}
                </>
              ) : (
                <>
                  {groupDetails.data?.type !== 'level' &&
                    createTabWithLockIcon({
                      tabName: 'People',
                      isActive: activeTab === 'users',
                      onClick: () => navigate(`${url}/users${param}`),
                      tabDisable: false
                    })}
                  {/* Matches */}
                  {groupDetails.data?.type !== 'level' &&
                    !forMobile &&
                    createTabWithLockIcon({
                      tabName: 'Matches',
                      isActive: activeTab === 'matches',
                      onClick: () => navigate(`${url}/matches${param}`),
                      tabDisable: isMasterclass || !(groupSettings && isGroupUnlocked), // Disable for Course always
                      lockTooltip: isMasterclass ? 'This is not needed for the Course model' : '' // Tooltip for Course
                    })}
                  {/* Settings */}
                  {groupDetails.data?.type !== 'level' &&
                    !forMobile &&
                    createTabWithLockIcon({
                      tabName: 'Settings',
                      isActive: activeTab === 'settings',
                      onClick: () => navigate(`${url}/settings${param}`)
                    })}
                </>
              )}
            </Box>
          </Box>
        </Box>
      );
    }
    return null;
  };
  const checkSettings = () => {
    if (groupType === 'level') {
      return <Outlet />;
    }

    // Don't make routing decisions until the data backing them is ready,
    // otherwise we'd redirect prematurely on first paint.
    const routingDataReady = Boolean(allGroupData.data && groupRoleList.data);

    // orgLevelAdmin: Members & Settings tabs are retired in-program. Bounce
    // their legacy routes to the program's primary surface (Designer for
    // anything with lessons, otherwise Matches), and keep the unlock-nudge
    // pointed at a real surface too.
    if (orgLevelAdmin) {
      const primarySurface = hasDesigner ? 'designer' : 'matches';
      const baseUrl = `/app/programdetails/${orgId}/${coId}/${grpId}`;
      if (
        currentPath.pathname.includes('/settings') ||
        currentPath.pathname.includes('/users')
      ) {
        return <Navigate to={`${baseUrl}/${primarySurface}${param}`} />;
      }
      if (
        routingDataReady &&
        grpId !== 'admin' &&
        !mainGroup &&
        (!groupSettings || !isGroupUnlocked) &&
        orgSettings &&
        !currentPath.pathname.includes('/new-program') &&
        !currentPath.pathname.includes('/overview') &&
        !currentPath.pathname.includes('/insights') &&
        !currentPath.pathname.includes('/designer') &&
        !currentPath.pathname.includes('/matches')
      ) {
        return <Navigate to={`${baseUrl}/${primarySurface}${param}`} />;
      }

      // Guided Course / Hybrid have BOTH surfaces — a compact tab bar lets the
      // admin switch Designer ↔ Matches (the legacy program tab bar is retired
      // in this flow). Single-surface programs (Course / Mentorship) render the
      // Outlet bare.
      const onDesigner = currentPath.pathname.includes('/designer');
      const onMatches = currentPath.pathname.includes('/matches');
      if (hasDesigner && hasMatches && (onDesigner || onMatches)) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box
              sx={{
                display: 'inline-flex',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid #2A2A2A',
                alignSelf: 'flex-start',
                m: 2,
                mb: 0
              }}
            >
              {[
                { key: 'designer', label: 'Designer', active: onDesigner },
                { key: 'matches', label: 'Matches', active: onMatches }
              ].map((t) => (
                <Box
                  key={t.key}
                  role="button"
                  onClick={() => navigate(`${baseUrl}/${t.key}${param}`)}
                  sx={{
                    px: 2.5,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: t.active ? '#444444' : '#2A2A2A',
                    '&:not(:last-of-type)': { borderRight: '1px solid #2A2A2A' }
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: t.active ? 700 : 400, color: t.active ? '#FFFFFF' : '#B6B6B6' }}
                  >
                    {t.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            {/* Absolute inset gives the surface a definite-height containing
                block so MatchesMainPage's height:100% chain resolves (a
                flex-computed height alone leaves it blank). */}
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <Box sx={{ position: 'absolute', inset: 0 }}>
                <Outlet />
              </Box>
            </Box>
          </Box>
        );
      }
      return <Outlet />;
    }

    if (
      routingDataReady &&
      grpId !== 'admin' &&
      !mainGroup &&
      (!groupSettings || !isGroupUnlocked) &&
      !currentPath.pathname.includes('/settings') &&
      orgSettings &&
      !currentPath.pathname.includes('/users') &&
      // Learning Framework: design-stage programs continue through the
      // /new-program/* creation funnel. The unlock-guard above would
      // otherwise yank the admin back to /settings the moment they land
      // on /new-program/start, before they can pick masterclass /
      // mentorship. /overview is also exempt so that navigating back
      // from the creation funnel lands on the dashboard, not settings.
      !currentPath.pathname.includes('/new-program') &&
      !currentPath.pathname.includes('/overview')
    ) {
      return <Navigate to={`/app/programdetails/${orgId}/${coId}/${grpId}/settings?section=members`} />;
    }
    return <Outlet />;
  };

  if (allGroupData.error) {
    return <Box>{allGroupData.errorText}</Box>;
  }
  return checkSettings();
};

export default ProgramDetails;
